import React, { useEffect } from "react";
import {
  useForm,
  Controller,
  useFieldArray,
  SubmitHandler,
} from "react-hook-form";
import {
  TextField,
  Button,
  Box,
  MenuItem,
  Typography,
  Grid,
  Paper,
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Autocomplete,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SaveIcon from "@mui/icons-material/Save";
import { v4 as uuidv4 } from "uuid";
import {
  useAddFamilyMembers,
  useGetAllFamily,
} from "../../hooks/query/useFamily";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAllProblems } from "../../hooks/query/useProblem";
import { Problem } from "../../types/Problem";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import FallbackLoader from "../../components/loading/FallBackLoader";
// import DeleteIcon from "@mui/icons-material/Delete";

// Define interfaces for form data
interface FamilyMember {
  id: string;
  name: string;
  dob: string;
  gender: string;
  relationship: string;
  problems: Problem[];
}

interface FormData {
  members: FamilyMember[];
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#bb0000",
    },
    background: {
      default: "#f4f4f4",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
          transition: "transform 0.3s ease-in-out",
          "&:hover": {
            transform: "scale(1.02)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& label.Mui-focused": {
            color: "#bb0000",
          },
          "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
              borderColor: "#bb0000",
            },
          },
        },
      },
    },
  },
});

// const apiProblems: Problem[] = [
//   { id: "1", displayName: "Allergy", systemName: "allergy" },
//   { id: "2", displayName: "Diabetes", systemName: "diabetes" },
//   { id: "3", displayName: "Hypertension", systemName: "hypertension" },
//   { id: "4", displayName: "Asthma", systemName: "asthma" },
// ];

const AddFamilyMembers: React.FC = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  if (!propertyId) {
    throw new Error("Property not found");
  }
  const { data: familyMembers, isLoading } = useGetAllFamily(propertyId);

  const { data: apiProblems } = useGetAllProblems();

  // Initialize form state only after data is loaded
  const { handleSubmit, control, reset } = useForm<FormData>({
    defaultValues: {
      members: [],
    },
  });

  // When familyMembers data is available, update the form state
  useEffect(() => {
    if (familyMembers) {
      reset({
        members: familyMembers?.familyMembers?.map((member) => ({
          id: member.id || uuidv4(),
          name: member.name || "",
          dob: member.dob || "",
          gender: member.gender || "Male",
          relationship: member.relationship || "",
          problems: member.problemEffects || [],
        })),
      });
    }
  }, [familyMembers, reset]);

  // Use useFieldArray to manage dynamic members
  const { fields, append, remove } = useFieldArray({
    control,
    name: "members",
  });

  console.log(fields);

  const { mutate: addFamilyMembers } = useAddFamilyMembers();

  const onSubmit: SubmitHandler<FormData> = (data) => {
    console.log("Submitted data:", data);
    addFamilyMembers({
      requestBody: data.members.map((member) => ({
        name: member.name,
        relationship: member.relationship,
        // occupation: member.occupation || "",
        dob: member.dob || "",
        // dot: member.dot || "",
        // birthPlace: member.birthPlace || "",
        problemFacing: member.problems.map((value) => value.id) || [],
      })),

      propertyId: propertyId,
    });
  };

  const handleSkip = () => {
    navigate(`/next-route/${propertyId}`);
  };

  if (isLoading) {
    return <FallbackLoader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container
        maxWidth="md"
        sx={{
          position: "relative",
          paddingBottom: "80px",
          padding: { xs: 0, sm: 1 },
        }}
      >
        <Box
          sx={{
            padding: { xs: 3, sm: 4 },
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            paddingBottom: "100px",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 10,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              endIcon={<SkipNextIcon />}
              onClick={handleSkip}
              sx={{
                borderColor: "#bb0000",
                color: "#bb0000",
                "&:hover": {
                  backgroundColor: "rgba(187, 0, 0, 0.04)",
                  borderColor: "#990000",
                },
              }}
            >
              Skip
            </Button>
          </Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              marginBottom: 3,
            }}
          >
            Family Member Registration
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            {fields.map((member, index) => (
              <Paper
                key={member.id}
                elevation={3}
                sx={{
                  padding: 3,
                  marginBottom: 3,
                  backgroundColor: "#f9f9f9",
                  position: "relative",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`members.${index}.name`}
                      control={control}
                      rules={{
                        required: "Name is required",
                        minLength: {
                          value: 2,
                          message: "Name must be at least 2 characters",
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label="Full Name"
                          variant="outlined"
                          fullWidth
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`members.${index}.dob`}
                      control={control}
                      rules={{ required: "Date of Birth is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label="Date of Birth"
                          type="date"
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`members.${index}.gender`}
                      control={control}
                      rules={{ required: "Gender is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Gender"
                          select
                          variant="outlined"
                          fullWidth
                          required
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`members.${index}.relationship`}
                      control={control}
                      rules={{
                        required: "Relationship is required",
                        minLength: {
                          value: 2,
                          message: "Relationship must be at least 2 characters",
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label="Relationship"
                          variant="outlined"
                          fullWidth
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name={`members.${index}.problems`}
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          id={`problems-${member.id}`}
                          options={apiProblems ? apiProblems : []}
                          getOptionLabel={(option) => option.displayName || ""}
                          value={field.value || []}
                          onChange={(_, newValue) => field.onChange(newValue)}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const { key, ...tagProps } = getTagProps({
                                index,
                              });
                              return (
                                <Chip
                                  key={key}
                                  label={option.displayName}
                                  {...tagProps}
                                />
                              );
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="Health Problems"
                              placeholder="Select Problems"
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "100%",
                        // marginTop: -2,
                      }}
                    >
                      {fields.length > 1 && (
                        <Button
                          onClick={() => remove(index)}
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          size="small"
                          sx={{
                            marginBottom: 2,
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <Box
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "white",
                padding: 2,
                boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                zIndex: 1000,
              }}
            >
              <Container maxWidth="md">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      width: "100%",
                      justifyContent: "right",
                    }}
                  >
                    <Button
                      type="button"
                      variant="contained"
                      onClick={() =>
                        append({
                          id: uuidv4(),
                          name: "",
                          dob: "",
                          gender: "Male",
                          relationship: "",
                          problems: [],
                        })
                      }
                      startIcon={<PersonAddIcon />}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        backgroundColor: "#bb0000",
                        "&:hover": {
                          backgroundColor: "#990000",
                        },
                      }}
                    >
                      Add Member
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        backgroundColor: "#4CAF50",
                        "&:hover": {
                          backgroundColor: "#45a049",
                        },
                      }}
                    >
                      Submit Family Details
                    </Button>
                  </Box>
                </Box>
              </Container>
            </Box>
          </form>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default AddFamilyMembers;
